//Third Party Dependencies
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Row } from 'reactstrap';

//MHD General Purpose libraries
import { validateDateOfBirth, validateEmail, validatePhoneNumber, validateZipCode } from '../../lib/validation';
import { resolveGenderOptions } from '../../lib/gender';
import ActiveErrors from '../../components/common/activeErrors';
import { SectionHeading } from '../../components/common/standardComponents';
import { useAppDispatch, useAppSelector } from "../../store";
import { useAppHistory } from 'hooks';

//Specific Business and UI components
import * as routes from '../../routes';
import { loadPatientSavePageData } from '../../actions/patientActions';
import { setActivePatient, updatePatient, searchAddress, getAddress } from '../../actions/rd2RefactorActions';
import PatientEditForm from '../../components/patient/patientEditForm';
import { withRouter } from 'components/hoc';
import { requestPacDemographics } from '../../actions/patientAccessCuratorActions';

interface PatientDetails {
	memberId: string;
	groupNumber: string;
	firstName: string;
	middleName: string;
	lastName: string;
	dateOfBirth: string;
	guardianName: string;
	homePhone: string;
	mobilePhone: string;
	alternatePhone: string;
	addressLine1: string;
	addressLine2: string;
	cityName: string;
	stateId: number;
	zipCode: string;
	email: string;
	notes: string;
	gender?: string;
	stateCode?: string;
	patientPoolUniqueToken?: string;
};

interface SelectedAddressOption {
	label: string;
	value: any;
}

interface PatientPool {
	displayName: string;
	prefix: string;
	integrationUniqueToken: string;
	uniqueToken: string;
	orgId: string | null;
	patientPoolId: number;
}

function PatientEditView(props: any) {

	const patientConfig: any = useAppSelector((state: any) => state.config.patient);
	const activePatient: any = useAppSelector((state: any) => state.activePatient);
	const lookupTables: any = useAppSelector((state: any) => state.lookupTables);
	const stateList: any = useAppSelector((state: any) => state.page.stateList);
	const routePrefix: any = useAppSelector((state: any) => state.config.instance.routePrefix);
	const patientPools: PatientPool[] = useAppSelector((state: any) => state.session.patientPools);

	const params = props.params;

	const [isPatientLoaded, setIsPatientLoaded] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [isPacValidating, setIsPacValidating] = useState<boolean>(false);
	const [isPacValidated, setIsPacValidated] = useState<boolean>(false);
	const [patientDetails, setPatientDetails] = useState<any>({ ...activePatient.details });
	const [referenceId, setReferenceId] = useState<string>(params.referenceId);
	const [forceValidation, setForceValidation] = useState<boolean>(false);
	const [touchedInputs, setTouchedInputs] = useState<any>({});
	const [phoneForceInvalid, setPhoneForceInvalid] = useState<boolean>(false);
	const [phoneForceInvalidMessage, setPhoneForceInvalidMessage] = useState<string>("");
	const [zipCodeForceInvalid, setZipCodeForceInvalid] = useState<boolean>(false);
	const [addressInputValue, setAddressInputValue] = useState<string | number>('');
	const [selectedAddressOption, setSelectedAddressOption] = useState<SelectedAddressOption>({ label: 'Select...', value: {} });
	const [isAddressLoading, setIsAddressLoading] = useState<boolean>(false);
	const [pacDemographics, setPacDemographics] = useState<any>({});

	const dispatch = useAppDispatch();
	const history = useAppHistory();

	useEffect(() => {
		if (!stateList.length) dispatch(loadPatientSavePageData());
		// should we allow using "stale" cached patient data?
		const isPatientAlreadyActive = (activePatient.id === referenceId);
		if (isPatientAlreadyActive) {
			const isPatientAlreadyLoaded = isPatientAlreadyActive && (activePatient.details.firstName !== undefined);
			if (isPatientAlreadyLoaded) {
				setIsPatientLoaded(isPatientAlreadyLoaded);
			}
			// if already active, but not loaded, we assume our "caller" has already dispatched the action that triggers the load
			//else {
			//	this.props.dispatch(setActivePatient(patientId));
			//}
		}
		else {
			dispatch(setActivePatient(referenceId))
		}
	}, []);

	const handleCancel = (e: any) => {
		//TODO: dispatch an action for this?
		if (history.navigate.length > 0)
			history.goBack();
		else
			dispatch(routes.patientSearchUrl(routePrefix));
	}

	const handlePatientChange = (e: any) => {
		var target = e.target;
		setPatientDetails((prevDetails: any) => ({
			...prevDetails,
			[target.name]: target.value,
		}));
		setTouchedInputs((prevTouched: any) => ({
			...prevTouched,
			[target.name]: true,
		}));

		if (target.name === 'homePhone' || target.name === 'mobilePhone') {
			updatePhoneForceInvalid();
		}
		if (target.name === 'zipCode') {
			setZipCodeForceInvalid(false);
		}
	};

	const handleUpdatePatientInfoFromPac = (currentPatientDetailsFromPac: PatientDetails) => {
		setPatientDetails(currentPatientDetailsFromPac);
	};

	const handleValidate = (e: any) => {
		e.preventDefault();
		setForceValidation(true);
		let sanitizedPatientDetails = beforeSave();

		updatePhoneForceInvalid();

		if (validate()) {
			setIsPacValidating(true);
			dispatch(requestPacDemographics(sanitizedPatientDetails))
				.then((result: any) => {
					if (!result.error) {
						setPacDemographics(result.payload.data.demographicResponse.patientDemographics);
					}
					else {
						setPacDemographics({});
					}
					setIsPacValidating(false);
					setIsPacValidated(true);
				});
		}
	};

	const handleSave = (e: any) => {
		e.preventDefault();

        setForceValidation(true);
		let sanitizedPatientDetails = beforeSave();

		updatePhoneForceInvalid();

		if (validate()) {
            setIsSaving(true);
			dispatch(updatePatient(sanitizedPatientDetails))
				.then((result: any) => {
					if (result.error) {
						setIsSaving(false);
					} else if (result.payload.data.errors && result.payload.data.errors.length > 0) {
						resolveSaveErrors(result.payload.data.errors);
					} else {
						return dispatch(setActivePatient(result.payload.data.patientDetails.referenceId));
					}
				})
				.then((result: any) => {
					if (result) {
						return dispatch(routes.patientDetails(routePrefix, result.payload.data.referenceId));
					}
				});
		}
	}

	const resolveSaveErrors = (errors: any) => {
		let currentZipCodeForceInvalid = false;
		if (errors && errors.length > 0) {
			currentZipCodeForceInvalid = errors.includes('zipcode');
		}
		setZipCodeForceInvalid(currentZipCodeForceInvalid);
		setIsSaving(false);
	};

	const beforeSave = () => {
		let currentPatientDetails = { ...patientDetails };
		const state = currentPatientDetails.stateId != null ? stateList.find((x: any) => x.id === Number(currentPatientDetails.stateId)) : null;
		currentPatientDetails.memberId = currentPatientDetails.memberId != null ? currentPatientDetails.memberId.trim() : null;
		currentPatientDetails.groupNumber = currentPatientDetails.groupNumber != null ? currentPatientDetails.groupNumber.trim() : null;
		currentPatientDetails.firstName = currentPatientDetails.firstName != null ? currentPatientDetails.firstName.trim() : null;
		currentPatientDetails.middleName = currentPatientDetails.middleName != null ? currentPatientDetails.middleName.trim() : null;
		currentPatientDetails.lastName = currentPatientDetails.lastName != null ? currentPatientDetails.lastName.trim() : null;
		currentPatientDetails.dateOfBirth =
			currentPatientDetails.dateOfBirth != null
				? moment.utc(currentPatientDetails.dateOfBirth.trim()).format('MM/DD/YYYY').toString()
				: null;
		currentPatientDetails.gender = currentPatientDetails.gender != null ? currentPatientDetails.gender.trim() : null;
		currentPatientDetails.homePhone = currentPatientDetails.homePhone != null ? currentPatientDetails.homePhone.trim() : null;
		currentPatientDetails.mobilePhone = currentPatientDetails.mobilePhone != null ? currentPatientDetails.mobilePhone.trim() : null;
		currentPatientDetails.alternatePhone = currentPatientDetails.alternatePhone != null ? currentPatientDetails.alternatePhone.trim() : null;
		currentPatientDetails.cityName = currentPatientDetails.cityName != null ? currentPatientDetails.cityName.trim() : null;
		currentPatientDetails.zipCode = currentPatientDetails.zipCode != null ? currentPatientDetails.zipCode.trim() : null;
		currentPatientDetails.email = currentPatientDetails.email != null ? currentPatientDetails.email.trim() : null;
		currentPatientDetails.notes = currentPatientDetails.notes != null ? currentPatientDetails.notes.trim() : null;
		currentPatientDetails.stateCode = currentPatientDetails.stateId != null ? state.name : null;

		return currentPatientDetails;
	};

	const shouldValidate = (name: string) => {
		return forceValidation || touchedInputs[name] === true;
	};

	const updatePhoneForceInvalid = () => {
		let bothPhonesEmpty =
			!patientConfig.homePhone.isRequired &&
			!patientConfig.mobilePhone.isRequired &&
			!patientDetails.homePhone &&
			!patientDetails.mobilePhone;
		setPhoneForceInvalid(bothPhonesEmpty);
	};

	const validate = () => {
		let memberIdValid = patientConfig.memberId.isRequired ? patientDetails.memberId !== '' : true;
		let groupNumberValid = patientConfig.groupNumber.isRequired ? patientDetails.groupNumber !== '' : true;
		let firstNameValid = patientConfig.firstName.isRequired ? patientDetails.firstName !== '' : true;
		let middleNameValid = patientConfig.middleName.isRequired ? patientDetails.middleName !== '' : true;
		let lastNameValid = patientConfig.lastName.isRequired ? patientDetails.lastName !== '' : true;
		let dobValid = patientConfig.dateOfBirth.isRequired
			? validateDateOfBirth(patientDetails.dateOfBirth)
			: true;
		let genderValid = patientConfig.gender.isRequired ? validateGender() : true;
		let addressValid = validateAddress();
		let phonesValid = validatePhones();
		let alternatePhoneValid = validateAlternatePhone();
		let emailValid = patientConfig.email.isRequired
			? validateEmail(patientDetails.email)
			: patientDetails.email && patientDetails.email.trim() !== ''
				? validateEmail(patientDetails.email)
				: true;
		let notesValid = patientConfig.notes.isRequired ? patientDetails.notes !== '' : true;

		return (
			memberIdValid &&
			groupNumberValid &&
			firstNameValid &&
			middleNameValid &&
			lastNameValid &&
			dobValid &&
			genderValid &&
			addressValid &&
			phonesValid &&
			alternatePhoneValid &&
			emailValid &&
			notesValid
		);
	};

	const validateGender = () =>  {
		let result = (patientDetails.gender.toLowerCase() === 'm' || patientDetails.gender.toLowerCase() === 'f' || patientDetails.gender.toLowerCase() === 'u');
		return result
	}

	const validateAddress = () => {
		let address1Valid = patientConfig.addressLine1.isRequired ? patientDetails.addressLine1 !== '' : true;
		let address2Valid = patientConfig.addressLine2.isRequired
			? patientDetails.addressLine2 !== '' && patientDetails.addressLine2 !== null
			: true;
		let cityValid = patientConfig.city.isRequired ? patientDetails.cityName !== '' : true;
		let stateValid = patientConfig.state.isRequired ? patientDetails.stateId > 0 : true;
		let zipValid =
			patientConfig.zipCode.isRequired && !patientDetails.zipCode
				? false
				: !zipCodeForceInvalid && validateZipCode(patientDetails.zipCode);

		return address1Valid && address2Valid && cityValid && stateValid && zipValid;
	};

	const validatePhones = () => {
		let isValid = false;

		if (patientConfig.homePhone.isRequired) {
			isValid = validatePhoneNumber(patientDetails.homePhone);
		} else if (patientConfig.mobilePhone.isRequired) {
			isValid = validatePhoneNumber(patientDetails.mobilePhone);
		} else {
			let bothPhonesEmpty = !patientDetails.homePhone && !patientDetails.mobilePhone;
			let isHomePhoneValid =
				!patientDetails.homePhone || validatePhoneNumber(patientDetails.homePhone);
			let isMobilePhoneValid =
				!patientDetails.mobilePhone || validatePhoneNumber(patientDetails.mobilePhone);
			setPhoneForceInvalidMessage(bothPhonesEmpty ? 'Please provide a valid Home or Mobile Phone' : '');

			isValid = !bothPhonesEmpty && isHomePhoneValid && isMobilePhoneValid;
		}

		return isValid;
	};

	const validateAlternatePhone = () => {
		let isValid = true;

		if (patientConfig.alternatePhone.isRequired || !!patientDetails.alternatePhone) {
			isValid = validatePhoneNumber(patientDetails.alternatePhone);
		}

		return isValid;
	}

	const handleAddressChange = (possibleOption: SelectedAddressOption) => {
		if (possibleOption.value.address) {
			setIsAddressLoading(true);
			setSelectedAddressOption(possibleOption);
			const addressSearchCriteria = {
				query: possibleOption.value.address,
			};
			dispatch(getAddress(addressSearchCriteria))
				.then((response: any) => {
					const addressSearchResult = response.payload.data;
					const newPatientDetails = createPatientDetailsForAddress(addressSearchResult);
					setSelectedAddressOption(possibleOption);
					setIsAddressLoading(false);
					setPatientDetails(newPatientDetails);
				});
		}
		else {
			setSelectedAddressOption({ label: 'Select...', value: {} });
		}
	}

	const createPatientDetailsForAddress = (addressSearchResult: any) => {
		const {
			addressLine1,
			addressLine2,
			city,
			state: stateCode,
			zipCode
		} = addressSearchResult;
		const state = stateList.find((x: any) => x.name === stateCode);
		const newPatientDetails = {
			...patientDetails,
			addressLine1: addressLine1,
			addressLine2: addressLine2,
			cityName: city,
			stateId: state ? state.id : 0,
			zipCode: zipCode.substring(0, 5),
		};

		return newPatientDetails;
	}

	const loadAddressOptions = async (inputValue: number, callback: any) => {
		setAddressInputValue(inputValue);
		if (inputValue < 5) {
			callback([]);
			return;
		}
		const result = await dispatch(searchAddress({ query: addressInputValue }));
		if (result.error) {
			console.error(result.error, addressInputValue);
			callback([]);
			return;
		}
		const addressSearchResults = result.payload.data;
		const addressOptions = addressSearchResults.map((a: any) => ({ label: a.address, value: a }));
		callback(addressOptions);
	}

	let genderOptions = resolveGenderOptions(
		patientConfig.allowUnknownGender,
		patientConfig.unknownGenderOptionText,
	);

	return (
		<div>
			{isPatientLoaded &&
				<>
					<ActiveErrors />
					<SectionHeading label="Edit Patient" />
					<Row noGutters>
						<div className="col-lg-12">
							<PatientEditForm
							patientConfig={patientConfig}
							genderOptions={genderOptions}
							isSaving={isSaving}
							lookupTables={lookupTables}
							onCancel={handleCancel}
							onPatientChange={handlePatientChange}
							onSave={handleSave}
							patientDetails={patientDetails}
							phoneForceInvalid={phoneForceInvalid}
							phoneForceInvalidMessage={phoneForceInvalidMessage}
							shouldValidate={shouldValidate}
							stateList={stateList}
							zipCodeForceInvalid={zipCodeForceInvalid}
							handleAddressChange={handleAddressChange}
							loadAddressOptions={loadAddressOptions}
							isAddressLoading={isAddressLoading}
							selectedAddressOption={selectedAddressOption}
							isPacValidated={isPacValidated}
							isPacValidating={isPacValidating}
							handleValidate={handleValidate}
							pacDemographics={pacDemographics}
							handleUpdatePatientInfoFromPac={handleUpdatePatientInfoFromPac}
							patientPools={patientPools}
							disablePatientPoolSelection={true}
							/>
						</div>
					</Row>
				</>
			}
		</div>
	)
}

export default withRouter(PatientEditView);
